import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddProductionBatch() {
  const [description, setDescription] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [productionBatchCode, setProductionBatchCode] = useState("");
  const [createdDate, setCreatedDate] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false); // Toggle save button visibility
  const [currentUser, setCurrentUser] = useState(""); // To hold the current user's information
  const hasFetched = useRef(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedProductIdsForTransfer, setSelectedProductIdsForTransfer] = useState([]);
  const [selectedProductIdsForReTransfer, setSelectedProductIdsForReTransfer] = useState([]);
  const [searchTermAvailable, setSearchTermAvailable] = useState("");
  const [searchTermSelected, setSearchTermSelected] = useState("");



  // Fetch user data, products, and locations
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/specific`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const userData = response.data;
        setCurrentUser(userData);

        // Set Created By field
        setCreatedBy(userData.username);

        // Fetch locations and products based on role
        if (userData.role === "Main Admin") {
          fetchAllLocations();
          fetchAllProducts();
        } else if (userData.role === "Company Admin") {
          fetchFilteredLocations(userData);
          fetchFilteredProducts(userData);
        }else if (userData.role === "Farm User") {
          fetchFilteredLocations(userData);
          fetchFilteredProducts(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    fetchCurrentUser();
  }, []);

    const fetchAllLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/locations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (currentUser.role === "Farm User") {
        setLocations(response.data.locations || []);
      } else {
        setLocations(response.data.locations);
      }   
    };

    const fetchAllProducts = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProducts(response.data.products);
    };

    const fetchFilteredLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-locations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (currentUser.role === "Farm User") {
        setLocations(response.data.locations || []);
      } else {
        setLocations(response.data.locations);
      }
      console.log("Locations:", response.data.locations);
    };


    const fetchFilteredProducts = async (userData) => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-products?organization=${userData.organization._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProducts(response.data.products);
    };

  // Generate Batch Code on load
  useEffect(() => {

    if (!currentUser) return;
    if (hasFetched.current) return;
  
    hasFetched.current = true; 

    const generateBatchCode = async () => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
  
      try {
        // Fetch organization prefix from the new endpoint
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE_URL}/organizations/prefix`, // New endpoint to fetch only the prefix
          {
            headers: {
              Authorization: `Bearer ${token}`, // Send the token in the header
            },
          }
        );
  
        const organizationPrefix = response.data.organization_prefix; // Organization prefix returned from the backend
        
        const batchNumberResponse = await axios.get(
          `${API_BASE_URL}/products/production-batch/latest-batch-number`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        const batchNumber = batchNumberResponse.data.nextBatchNumber;

        console.log("batchNumber:", batchNumber);
  
        // Generate batch code with organization prefix
        const code = `${organizationPrefix}-PB-${year}-${month}-${batchNumber}`;
        setProductionBatchCode(code);
        setCreatedDate(new Date().toLocaleString());
        setCreatedBy(currentUser?.username || "Unknown User"); // Replace with the logged-in user
      } catch (error) {
        console.error("Error fetching batch number:", error);
        toast.error("Failed to generate batch number.");
      }
    };
  
    if (currentUser) generateBatchCode();
  }, [currentUser]); // Dependency array now includes currentUser
  
  

  const handleAddProducts = async () => {

    // Disable the button after click
    setIsButtonDisabled(true);
    
    if (!description || !selectedLocation) {
      toast.error("All fields are required");
      return;
    }

    // Prepare payload for creating a production batch
    const payload = {
      productionBatchCode,
      description,
      location: selectedLocation,
      selectedProducts: selectedProducts.map((product) => ({
        product_name: product.product_name,
        product_image: product.product_image,
        unit: product.unit?.unit_name,
        in_stock_quantity: product.in_stock_quantity || 0, // default to 0 if undefined
        price: product.price || 0, // default to 0 if undefined
      })),
    };

    try {
      // Get the token from localStorage (or sessionStorage)
      const token = localStorage.getItem("token");

      // Ensure token is available  
      if (!token) {
        toast.error("No authentication token found, please login again.");
        return;
      }

      // Send data to backend with Authorization header
      const response = await axios.post(
        `${API_BASE_URL}/products/production-batch/add`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to the request headers
          },
        }
      );

      toast.success("Production Batch created successfully!");

      // Store the batch code to use when adding products
      const createdBatchCode = response.data.productionBatchCode; // Get batch code from response
      setProductionBatchCode(createdBatchCode); // Set the correct batch code
      setIsModalOpen(true); // Open modal to add products
    } catch (error) {
      setIsButtonDisabled(false); // Re-enable button if an error occurs
      console.error("Error creating batch:", error);
      toast.error("Failed to create Production Batch");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle checkbox change for Available Products
  const handleCheckboxChangeForTransfer = (e, row) => {
    const { _id } = row;
    let updatedIds = e.target.checked
      ? [...selectedProductIdsForTransfer, _id]
      : selectedProductIdsForTransfer.filter(id => id !== _id);
    
    setSelectedProductIdsForTransfer(updatedIds);
  };

  // Handle checkbox change for Selected Products
  const handleCheckboxChangeForReTransfer = (e, row) => {
    const { _id } = row;
    let updatedIds = e.target.checked
      ? [...selectedProductIdsForReTransfer, _id]
      : selectedProductIdsForReTransfer.filter(id => id !== _id);
    
    setSelectedProductIdsForReTransfer(updatedIds);
  };

  // Select all checkboxes handling for Available Products
  const handleSelectAllClickForTransferProduct = (event) => {
    setSelectedProductIdsForTransfer(event.target.checked ? products.map(p => p._id) : []);
  };

  // Select all checkboxes handling for Selected Products
  const handleSelectAllClickForReTransferProduct = (event) => {
    setSelectedProductIdsForReTransfer(event.target.checked ? selectedProducts.map(p => p._id) : []);
  };

  const handleTransferProduct = () => {
    const toTransfer = products.filter(p => selectedProductIdsForTransfer.includes(p._id));
    setProducts(prev => prev.filter(p => !selectedProductIdsForTransfer.includes(p._id)));
    setSelectedProducts(prev => [...prev, ...toTransfer]);
    setSelectedProductIdsForTransfer([]); // Clear selection after transfer
  };
  
  const handleReTransferProduct = () => {
    const toReTransfer = selectedProducts.filter(p => selectedProductIdsForReTransfer.includes(p._id));
    setSelectedProducts(prev => prev.filter(p => !selectedProductIdsForReTransfer.includes(p._id)));
    setProducts(prev => [...prev, ...toReTransfer]);
    setSelectedProductIdsForReTransfer([]); // Clear selection after transfer
  };
  

  // Save the selected products with quantity and price to the main grid
  const handleSaveProducts = async () => {
    const selectedProductsData = selectedProducts.map((product, index) => ({
      id: product.id || index, // Unique ID for DataGrid
      product_code: product.product_code, // Use product_code as the identifier
      product_image: product.product_image,
      product_name: product.product_name, // Ensure the required fields exist
      unit: product.unit?.unit_name,
      in_stock_quantity: product.in_stock_quantity ?? 0, // Initially set quantity to 0
      price: product.price ?? 0, // Initially set price to 0
    }));

    // Prepare payload to add products
    const payload = {
      productionBatchCode,
      selectedProducts: selectedProductsData,
    };

    try {
      // Get the token from localStorage (or sessionStorage)
      const token = localStorage.getItem("token");

      // Ensure token is available
      if (!token) {
        toast.error("No authentication token found, please login again.");
        return;
      }

      // Send products to backend
      await axios.put(
        `${API_BASE_URL}/products/production-batch/add-products`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to the request headers
          },
        }
      );
      toast.success("Products added successfully!");

      // Reset products and close the modal
      setSelectedProducts(selectedProductsData);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding products:", error);
      toast.error("Failed to add products");
    }
  };

  // Handle the update of quantity and price in the DataGrid
  const handleQtyPriceChange = (updatedRow) => {
    try {
      const updatedBatchProducts = selectedProducts.map((product) => {
        if (product.id === updatedRow.id) {
          return {
            ...product,
            in_stock_quantity: Math.max(0,Number(updatedRow.in_stock_quantity)),
            price: parseFloat(updatedRow.price) || 0, // Keep as a number
          };
        }
        return product;
      });

      setSelectedProducts(updatedBatchProducts);
      setSaveEnabled(true);
      return { ...updatedRow, isNew: false };
    } catch (error) {
      console.error("Error processing row update:", error);
      toast.error("Error processing row update");
      throw error;
    }
  };

  //function to filter available products dynamically according to the search term
  const filteredAvailableProducts = products.filter((product) =>
    product.product_name.toLowerCase().includes(searchTermAvailable.toLowerCase())
  );
  
  //function to filter selected products dynamically according to the search term
  const filteredSelectedProducts = selectedProducts.filter((product) =>
    product.product_name.toLowerCase().includes(searchTermSelected.toLowerCase())
  );  

  // Handle the saving of updated batch products (quantity and price)
  const handleSaveBatchProducts = async () => {
    try {
      // Get the token from localStorage (or sessionStorage)
      const token = localStorage.getItem("token");

      // Ensure token is available
      if (!token) {
        toast.error("No authentication token found, please login again.");
        return;
      }

      const payload = {
        productionBatchCode: productionBatchCode,
        updatedProducts: selectedProducts.map((product) => {
          const productId =
            product.product && product.product.product_code
              ? product.product.product_code
              : product.product_code;
          if (!productId) {
            throw new Error("Invalid product data, _id is missing.");
          }
          return {
            product_code: productId.toString(), // Use the product code as the identifier
            in_stock_quantity: Number(product.in_stock_quantity),
            unit : product.unit?.unit_name,
            price: parseFloat(product.price).toFixed(2), // Ensure the price is formatted to two decimal places
          };
        }),
      };

      console.log("Payload being sent:", payload);

      await axios.put(
        `${API_BASE_URL}/products/production-batch/update-products`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to the request headers
          },
        }
      );

      toast.success("Batch products updated successfully!");

      setSaveEnabled(false); // Disable the save button after saving
    } catch (error) {
      console.error("Error saving batch products:", error);

      // Extract error message from backend response
      let errorMessage = "Failed to save products.";
      if (error.response) {
        errorMessage = error.response.data?.message || "An error occurred while updating batch products.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <div className="container mx-auto p-8">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">
          Add Production Batch
        </h2>

        <div className="grid grid-cols-3 gap-8">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">
              Production Batch Code
            </label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg"
              value={productionBatchCode}
              readOnly
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">
              Created Date & Time
            </label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg"
              value={createdDate}
              readOnly
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Created By</label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg"
              value={createdBy}
              readOnly
            />
          </div>
          <div className="mb-4 col-span-3">
            <label className="block text-gray-700 mb-2">Description</label>
            <textarea
              className="w-full p-3 border rounded-lg"
              placeholder="Enter production batch description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Location</label>
            <select
              className="w-full p-3 border rounded-lg"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="" disabled>
                Select Location
              </option>
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.location_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-6 text-right">
          {/* {!isButtonDisabled && ( */}
            <button
              onClick={handleAddProducts}
              className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
            >
              Add Products
            </button>
          {/* )} */}
        </div>

        {/* Display Selected Products with qty and price */}
        {selectedProducts.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4">
              Selected Products for Production Batch
            </h3>
            <div style={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={selectedProducts}
                getRowId={(row) => row.product_code || row._id || row.id} // Use `_id` as the unique identifier for each row
                columns={[
                  { field: "product_code", headerName: "ID", width: 100, flex: 0.6 },
                  {
                    field: "product_name",
                    headerName: "Product Name",
                    width: 200,
                    flex: 1,
                  },
                  {
                    field: "product_image",
                    headerName: "Image",
                    width: 150,
                    flex: 1,
                    renderCell: (params) =>
                      params.row.product_image ? (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                          alt="Product"
                          className="h-16 w-16 object-cover rounded-md"
                        />
                      ) : (
                        "No Image"
                      ),
                  },    
                  {
                    field: "in_stock_quantity",
                    headerName: "Quantity",
                    width: 150,
                    editable: true, // Allow editing quantity
                    type: "number",
                    flex: 1,
                    align: "right", 
                    headerAlign: "right", 
                    renderEditCell: (params) => {
                      return (
                        <input
                          type="number"
                          min="0" // Prevent negative values
                          autoFocus // Focus immediately
                          defaultValue={params.value}
                          style={{
                            width: "100%",
                            height: "100%",
                            padding: "5px",
                            boxSizing: "border-box",
                            border: "none",
                          }}
                          onKeyDownCapture={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault(); // Prevent default DataGrid behavior
                              const newValue = Math.max(0, parseFloat(e.target.value) || 0);
                              //{new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}
                  
                              // Update the value using setEditCellValue
                              params.api.setEditCellValue({
                                id: params.id,
                                field: params.field,
                                value: newValue,
                              });
                  
                              // Stop edit mode
                              params.api.stopCellEditMode({
                                id: params.id,
                                field: params.field,
                              });
                            }
                          }}
                        />
                      );
                    },
                  },
                  {
                    field: "unit",
                    headerName: "Unit",
                    flex: 1,
                    headerAlign: "center",
                    align: "center",
                  },                 
                  {
                    field: "price",
                    headerName: "Price (LKR)",
                    width: 150,
                    editable: true,
                    type: "number",
                    flex: 1,
                    align: "right",
                    headerAlign: "right", 
                    renderEditCell: (params) => {
                      return (
                        <input
                          type="number"
                          step="0.01" // Allow decimals
                          min="0"
                          autoFocus // Focus immediately
                          defaultValue={params.value}
                          style={{
                            width: "100%",
                            height: "100%",
                            padding: "5px",
                            boxSizing: "border-box",
                            border: "none",
                          }}
                          onKeyDownCapture={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault(); // Prevent default DataGrid behavior
                              const newValue = Math.max(0, parseFloat(e.target.value) || 0);
                  
                              // Update the value using setEditCellValue
                              params.api.setEditCellValue({
                                id: params.id,
                                field: params.field,
                                value: newValue,
                              });
                  
                              // Stop edit mode
                              params.api.stopCellEditMode({
                                id: params.id,
                                field: params.field,
                              });
                            }
                          }}
                        />
                      );
                    },
                  },
                ]}
                pageSize={5}
                processRowUpdate={handleQtyPriceChange} // Handle editing in the grid
                onProcessRowUpdateError={(error) => {
                  console.error("Row update error:", error); // Log the error for debugging
                  toast.error("Error updating row: " + error.message); // Show a toast message to the user
                }}
              />
            </div>
            {/* Display Save button if changes are made */}
            {saveEnabled && (
              <div className="mt-6 text-right">
                <button
                  onClick={handleSaveBatchProducts}
                  className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
                >
                  Save Products
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-20 w-4/4">
            <h3 className="text-2xl font-semibold mb-4">Manage Products</h3>
            <div className="grid grid-cols-3 gap-4">
              
              {/* Available Products */}
              <div>
                <h4 className="text-xl font-bold mb-2">Available Products</h4>
                
                {/* Search Input for Available Products */}
                <input
                  type="text"
                  placeholder="Search Available Products"
                  value={searchTermAvailable}
                  onChange={(e) => setSearchTermAvailable(e.target.value)}
                  className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg"
                />

                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={filteredAvailableProducts} // Use the filtered list
                    getRowId={(row) => row._id}
                    columns={[
                      {
                        field: "select",
                        headerName: "Select",
                        width: 100,
                        renderHeader: (params) => (
                          <input
                            type="checkbox"
                            checked={selectedProductIdsForTransfer.length === filteredAvailableProducts.length && filteredAvailableProducts.length > 0}
                            onChange={handleSelectAllClickForTransferProduct}
                            indeterminate={selectedProductIdsForTransfer.length > 0 && selectedProductIdsForTransfer.length < filteredAvailableProducts.length}
                          />
                        ),
                        renderCell: (params) => (
                          <input
                            type="checkbox"
                            checked={selectedProductIdsForTransfer.includes(params.row._id)}
                            onChange={(e) => handleCheckboxChangeForTransfer(e, params.row)}
                          />
                        ),
                      },
                      { field: "product_code", headerName: "ID", width: 100 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                          params.row.product_image ? (
                            <img
                              src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                              alt="Product"
                              className="h-16 w-16 object-cover rounded-md"
                            />
                          ) : (
                            "No Image"
                          ),
                      },
                      { 
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                      },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>

              {/* Transfer Buttons */}
              <div className="flex flex-col items-center justify-center">
                <button
                  onClick={handleTransferProduct}
                  className="bg-green-600 text-white py-2 px-3 rounded-lg mb-4"
                >
                  Transfer &gt;&gt;
                </button>
                <button
                  onClick={handleReTransferProduct}
                  className="bg-red-600 text-white py-2 px-3 rounded-lg"
                >
                  &lt;&lt; Remove
                </button>
              </div>

              {/* Selected Products */}
              <div>
                <h4 className="text-xl font-bold mb-2">Selected Products</h4>

                {/* Search Input for Selected Products */}
                <input
                  type="text"
                  placeholder="Search Selected Products"
                  value={searchTermSelected}
                  onChange={(e) => setSearchTermSelected(e.target.value)}
                  className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg"
                />

                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={filteredSelectedProducts} // Use the filtered list
                    getRowId={(row) => row._id}
                    columns={[
                      {
                        field: "select",
                        headerName: "Select",
                        width: 100,
                        renderHeader: (params) => (
                          <input
                            type="checkbox"
                            checked={selectedProductIdsForReTransfer.length === filteredSelectedProducts.length && filteredSelectedProducts.length > 0}
                            onChange={handleSelectAllClickForReTransferProduct}
                            indeterminate={selectedProductIdsForReTransfer.length > 0 && selectedProductIdsForReTransfer.length < filteredSelectedProducts.length}
                          />
                        ),
                        renderCell: (params) => (
                          <input
                            type="checkbox"
                            checked={selectedProductIdsForReTransfer.includes(params.row._id)}
                            onChange={(e) => handleCheckboxChangeForReTransfer(e, params.row)}
                          />
                        ),
                      },
                      { field: "product_code", headerName: "ID", width: 100 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                          params.row.product_image ? (
                            <img
                              src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                              alt="Product"
                              className="h-16 w-16 object-cover rounded-md"
                            />
                          ) : (
                            "No Image"
                          ),
                      },
                      {
                        field: "product_name", 
                        headerName: "Product Name",
                        width: 150
                      },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 text-right">
              <button
                onClick={handleSaveProducts}
                className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
              >
                Save
              </button>
              <button
                onClick={handleCloseModal}
                className="bg-red-600 text-white py-3 px-6 rounded-lg ml-4 hover:bg-gray-400 transition duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AddProductionBatch;
